import React, { useState } from 'react';
import { Group, Button, Text, Box, ActionIcon } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconHeart, IconHeartFilled, IconShare, IconCopy } from '@tabler/icons-react';

interface InteractionButtonsProps {
  context: string[]; // e.g., ['post'], ['event'], ['artist']
  postId?: number;
  likeCount?: number;
  isLiked?: boolean;
  isUserSignedIn: boolean;
  isAuthor?: boolean;
  isSuperAdmin?: boolean;
  metaImageUrl?: string;
  object?: any;
  onLike?: (postId: number) => void;
}

const InteractionButtons: React.FC<InteractionButtonsProps> = ({
  context = [],
  postId,
  likeCount = 0,
  isLiked = false,
  isUserSignedIn = false,
  isAuthor = false,
  isSuperAdmin = false,
  object,
  onLike,
  metaImageUrl,
}) => {
  const [liked, setLiked] = useState(isLiked);
  const [likes, setLikes] = useState(likeCount);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const shareTitle = context.includes('post')
    ? document.title
    : context.includes('event')
      ? `Check out this event: ${object?.name || 'an awesome event'}`
      : context.includes('artist')
        ? `Discover this artist: ${object?.name || 'an amazing artist'}`
        : context.includes('area')
          ? `Explore dance events in the ${object?.name || 'this area'}`
          : context.includes('dance_style')
            ? `Learn more about ${object?.name || 'this dance style'}`
            : 'Check this out!';

  const handleShare = async () => {
    try {
      const metaTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content') || shareTitle;
      const metaUrl =
        document.querySelector('meta[property="og:url"]')?.getAttribute('content') || window.location.href;
      const metaDescription = document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '';

      console.log('[handleShare] metaTitle:', metaTitle);
      console.log('[handleShare] metaUrl:', metaUrl);
      console.log('[handleShare] metaDescription:', metaDescription);
      console.log('[handleShare] metaImageUrl (prop):', metaImageUrl);

      // Check if Web Share API is available
      if (!navigator.share) {
        alert('Sharing is not supported on this browser. You can copy this URL: ' + metaUrl);
        return;
      }

      // First try URL-only sharing (most compatible)
      try {
        await navigator.share({
          title: metaTitle,
          text: metaDescription,
          url: metaUrl,
        });
        console.log('[handleShare] Basic URL share succeeded');
        return;
      } catch (basicShareError) {
        console.error('[handleShare] Basic URL share failed:', basicShareError);
        // If user canceled sharing, don't try other methods
        if (basicShareError.name === 'AbortError') {
          return;
        }
        // Continue to try with image if available
      }

      // Only attempt image sharing if metaImageUrl exists and device supports file sharing
      if (navigator.canShare && metaImageUrl) {
        try {
          // Test if file sharing is supported
          if (!navigator.canShare({ files: [new File([], 'test.txt')] })) {
            throw new Error('File sharing not supported on this device');
          }

          console.log('[handleShare] Attempting to fetch image');
          const response = await fetch(metaImageUrl, {
            mode: 'cors',
            credentials: 'omit',
          });

          if (!response.ok) {
            throw new Error(`Image fetch failed with status: ${response.status}`);
          }

          console.log('[handleShare] Fetch successful, creating blob');
          const blob = await response.blob();
          console.log('[handleShare] Blob created:', blob.type, blob.size);

          const file = new File([blob], 'preview.jpg', { type: blob.type });
          console.log('[handleShare] File created');

          console.log('[handleShare] Attempting share with image');
          await navigator.share({
            title: metaTitle,
            text: metaDescription,
            url: metaUrl,
            files: [file],
          });

          console.log('[handleShare] Share with image succeeded');
        } catch (imageError) {
          console.error('[handleShare] Image share failed:', imageError);

          // Final fallback - try one more time with just the URL
          try {
            console.log('[handleShare] Final fallback - URL only share');
            await navigator.share({
              title: metaTitle,
              text: metaDescription,
              url: metaUrl,
            });
            console.log('[handleShare] Final fallback share succeeded');
          } catch (fallbackError) {
            console.error('[handleShare] All sharing methods failed:', fallbackError);
            alert('Unable to share. You can manually copy this URL: ' + metaUrl);
          }
        }
      }
    } catch (error) {
      console.error('[handleShare] Outer error catch:', error);
      alert(
        `Something went wrong while trying to share this page. You can manually copy this URL: ${window.location.href}`,
      );
    }
  };

  const handleLike = () => {
    if (!isUserSignedIn || !postId || !onLike) return;
    onLike(postId);
    setLiked(!liked);
    setLikes(liked ? likes - 1 : likes + 1);
  };

  return (
    <Box
      style={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        paddingLeft: theme.spacing.xl,
        borderRadius: theme.radius.md,
        padding: theme.spacing.sm,
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.03)',
        backgroundColor: '#17171790',
        '&:hover': {
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.01)',
          transition: 'box-shadow 0.3s ease-in-out',
        },
      })}
    >
      {/* Like (only shown on post context) */}
      {context.includes('post') && (
        <>
          {isUserSignedIn ? (
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                color="#EA580C"
                size="lg"
                disabled // ⛔️ Still disabled
              >
                {liked ? <IconHeartFilled size={24} color="red" /> : <IconHeart size={24} color="red" />}
              </ActionIcon>
              <Text size="sm">{likes}</Text>
            </Group>
          ) : (
            <Button disabled variant="filled" color="#EA580C" className="clear-button" component="a">
              Like
            </Button>
          )}
        </>
      )}

      {/* Share Button */}
      <Button
        variant="outline"
        color="#EA580C"
        leftSection={<IconShare size={18} />}
        onClick={handleShare}
        className="clear-button"
      >
        Share
      </Button>
    </Box>
  );
};

export default InteractionButtons;
